import { useMemo, type HTMLAttributes } from 'react';

import { cn } from '@/shared/utils/cn';
import { mdConverter } from '@/shared/utils/md-converter';
import type { Element as DomElement } from 'domhandler';
import parse, { domToReact, type HTMLReactParserOptions } from 'html-react-parser';
import { showCitePostsDialog } from './cite-posts-dialog/show-cite-posts-dialog';

type ParsedCiteData = {
  postIds: string[];
  numCitedPosts: number;
};

const parseCiteData = (dataPostIds?: string, dataNumCitedPosts?: string): ParsedCiteData => {
  const postIds = (dataPostIds ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  const parsedNumCitedPosts = Number.parseInt(dataNumCitedPosts ?? '', 10);
  const numCitedPosts = Number.isNaN(parsedNumCitedPosts)
    ? postIds.length
    : Math.max(parsedNumCitedPosts, 0);

  return { postIds, numCitedPosts };
};

export const DeepResearchCard = (props: HTMLAttributes<HTMLDivElement>) => {
  const { children, className, ...rest } = props;
  return (
    <div className={cn('flex flex-col rounded-2xl bg-white p-6', className)} {...rest}>
      {children}
    </div>
  );
};

export const DeepResearchCompTitle = (props: HTMLAttributes<HTMLHeadingElement>) => {
  const { children, className, ...rest } = props;
  return (
    <h2 className={cn('text-3xl font-bold', className)} {...rest}>
      {children}
    </h2>
  );
};

const useMarkdownParseOptions = ({ onCiteClick }: { onCiteClick: (ids: string[]) => void }) => {
  const markdownParseOptions: HTMLReactParserOptions = useMemo(() => {
    return {
      replace(domNode) {
        if (domNode.type !== 'tag') return;

        const node = domNode as DomElement;
        const children = domToReact(node.children, markdownParseOptions);

        switch (node.name) {
          // Headings
          case 'h1':
            return <h1 className="mt-6 mb-4 text-3xl font-bold">{children}</h1>;
          case 'h2':
            return <h2 className="mt-5 mb-3 text-2xl font-bold">{children}</h2>;
          case 'h3':
            return <h3 className="mt-4 mb-3 text-xl font-semibold">{children}</h3>;
          case 'h4':
            return <h4 className="mt-3 mb-2 text-lg font-semibold">{children}</h4>;
          case 'h5':
            return <h5 className="mt-3 mb-2 text-base font-semibold">{children}</h5>;
          case 'h6':
            return <h6 className="mt-3 mb-2 text-sm font-semibold">{children}</h6>;

          // Paragraph
          case 'p':
            return <p className="mb-4">{children}</p>;

          // Links
          case 'a':
            return (
              <a
                className="text-blue-600 underline hover:text-blue-800"
                href={node.attribs.href}
                target={node.attribs.target}
                rel={node.attribs.rel}
              >
                {children}
              </a>
            );

          // Lists
          case 'ul':
            return <ul className="mb-4 ml-6 list-disc space-y-2">{children}</ul>;
          case 'ol':
            return <ol className="mb-4 ml-6 list-decimal space-y-2">{children}</ol>;
          case 'li':
            return <li className="leading-7">{children}</li>;

          // Emphasis
          case 'strong':
            return <strong className="font-bold">{children}</strong>;
          case 'em':
            return <em className="italic">{children}</em>;

          // Code
          case 'code':
            // Inline code (부모가 pre가 아닌 경우)
            if (node.parent && 'name' in node.parent && node.parent.name === 'pre') {
              return;
            }
            return (
              <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm text-gray-800">
                {children}
              </code>
            );
          case 'pre':
            return (
              <pre className="mb-4 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm">
                <code className="font-mono text-gray-100">{children}</code>
              </pre>
            );

          // Blockquote
          case 'blockquote':
            return (
              <blockquote className="mb-4 border-l-4 border-gray-300 pl-4 text-gray-600 italic">
                {children}
              </blockquote>
            );

          // Horizontal rule
          case 'hr':
            return <hr className="my-8 border-t border-gray-300" />;

          // Custom cite tag
          case 'cite': {
            const { postIds, numCitedPosts } = parseCiteData(
              node.attribs['data-post-ids'],
              node.attribs['data-num-cited-posts'],
            );
            return (
              <span
                className={cn(
                  'mx-1 inline-block -translate-y-0.5 cursor-pointer rounded-full border bg-gray-200 px-1.5 text-xs font-semibold transition-colors duration-200 hover:bg-gray-300',
                )}
                onClick={() => onCiteClick(postIds)}
              >
                {numCitedPosts}
              </span>
            );
          }

          // Table
          case 'table':
            return (
              <div className="mb-4 overflow-x-auto">
                <table className="min-w-full border-collapse border border-gray-300">
                  {children}
                </table>
              </div>
            );
          case 'thead':
            return <thead className="bg-gray-50">{children}</thead>;
          case 'tbody':
            return <tbody>{children}</tbody>;
          case 'tr':
            return <tr className="border-b border-gray-300">{children}</tr>;
          case 'th':
            return (
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
                {children}
              </th>
            );
          case 'td':
            return <td className="border border-gray-300 px-4 py-2">{children}</td>;

          default:
            return;
        }
      },
    };
  }, [onCiteClick]);
  return markdownParseOptions;
};

export const DeepResearchCompContent = (
  props: HTMLAttributes<HTMLParagraphElement> & { children: string },
) => {
  const markdownParseOptions = useMarkdownParseOptions({
    onCiteClick: (ids) => showCitePostsDialog(ids),
  });
  const { children, className, ...rest } = props;
  const htmlString = mdConverter.makeHtml(children);
  const parsed = parse(htmlString, markdownParseOptions);
  return (
    <div className={cn('text-base text-gray-700', className)} {...rest}>
      {parsed}
    </div>
  );
};
